import { TestImage } from "@assets/constant/index.constant";
import { CashIcon, InvoiceIcon } from "@assets/icons";
import { Button } from "@components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { Skeleton } from "@components/ui/skeleton";
import { endpoint } from "@services/api.services";
import { GeneralCard, TableCard } from "@ui/internal/components/card";
import { usePathname } from "@utils/hook/index.hook";
import { QueryMetaData } from "@utils/interface/index.interface";
import { PaymentBatchSummaryData } from "@utils/interface/journey.interface";
import { useListData } from "@utils/query/index.query";
import { Wallet } from "lucide-react";
import React from "react";
import { NavLink, useSearchParams } from "react-router";
import { ColumnList } from "./column/index.column";
import PaymentForm from "./form/index.form";
import { useMetaStore } from "@utils/states/index.state";
import { format } from "date-fns";
import FilterPopover from "@ui/internal/components/filter-popover";
type Props = {};

const SummaryStatCard = ({
  icon,
  label,
  value,
  isLoading,
}: {
  icon: React.ReactNode;
  label: string;
  value?: number | null;
  isLoading?: boolean;
}) => (
  <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
    <span className="p-2 max-w-[250px] max-h-[250px] flex items-center justify-center rounded-full border border=2">
      {icon}
    </span>
    <div>
      <span className="text-[#969696] text-nowrap text-[14px]">{label}</span>
      <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
        {isLoading ? (
          <Skeleton className="h-[50px] w-[90px]" />
        ) : (
          `NRs. ${(value ?? 0).toLocaleString()}`
        )}
      </h5>
    </div>
  </div>
);

const index: React.FC<Props> = ({}) => {
  const [searchParams] = useSearchParams();
  const pathname = usePathname();

  const columns = ColumnList();
  const { search, date } = useMetaStore();
  /* ================================================= api calls logic ================================================= */
  const filterParams = {
    search: search,
    created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
    created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
    course_batch: searchParams.get("course-batch") || null,
    payment_mode: searchParams.get("paymentmode") || null,
    student:
      searchParams.get("student__in") || searchParams.get("student") || "",
    is_masterclass: pathname == "masterclass-payment" ? true : false,
    is_jrp: pathname == "jrp-payment" ? true : false,
    course_type: searchParams.get("course-type"),
  };

  const { data, refetch, isPending } = useListData<
    QueryMetaData<any, PaymentBatchSummaryData>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      ordering: searchParams.get("ordering") || "order",
      ...filterParams,
    },
    endpoint: endpoint["payment"],
    key: `${pathname}-listing`,
    window: true,
  });

  const summary = data?.data?.revenue_summary;
  return (
    <>
      <TableCard
        bulkActions
        table_content={{
          mode: "default",
          type: "payment",
        }}
        tableHeaderData={
          summary ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 w-full justify-between items-center gap-4">
              {summary.total_paid_amount != null && (
                <SummaryStatCard
                  icon={<CashIcon />}
                  label="Paid Amount"
                  value={summary.total_paid_amount}
                  isLoading={isPending}
                />
              )}
              {summary.total_due_amount != null && (
                <SummaryStatCard
                  icon={<Wallet size={32} />}
                  label="Due Amount"
                  value={summary.total_due_amount}
                  isLoading={isPending}
                />
              )}
            </div>
          ) : undefined
        }
        inject_ui_table_actions={
          <>
            <FilterPopover
              filters={[
                {
                  label: "Course Batch",
                  endpointValue: "course-batch",
                  queries: {
                    is_masterclass:
                      pathname == "masterclass-payment" ? "true" : "false",
                  },
                  format: {
                    key: "name",
                    value: "id",
                  },
                },
                {
                  label: "Student",
                  endpointValue: "student",
                  format: {
                    key: "email",
                    value: "id",
                  },
                },
                {
                  label: "Payment Mode",
                  endpointValue: "paymentmode",
                  format: {
                    key: "name",
                    value: "id",
                  },
                },
                {
                  label: "Course Type",
                  endpointValue: "course-type",

                  format: {
                    key: "name",
                    value: "id",
                  },
                },
              ]}
              title="Filter"
              triggerText="Filter"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="defaultoutline">Manage Setup</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-fit" align="start">
                <DropdownMenuRadioGroup>
                  <NavLink to={"/payment/mode"}>
                    <DropdownMenuItem>Manage Pay Mode</DropdownMenuItem>
                  </NavLink>
                  <NavLink to={"/payment/discounts"}>
                    <DropdownMenuItem>Manage Discounts</DropdownMenuItem>
                  </NavLink>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        orientationPosition={false}
        endpoint={endpoint["payment"]}
        grid={
          <GeneralCard
            showCheck
            content={{
              image: TestImage,
              title: "Focused Mode",
              tags: ["Clarity"],
            }}
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        refetch={refetch}
        isPending={isPending}
        columns={columns}
        // columns={[]}
        sortableFilter={{
          search: search,
          created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
          created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
          course_batch: searchParams.get("course-batch") || null,
          payment_mode: searchParams.get("payment-mode") || null,
          is_masterclass: pathname == "masterclass-payment" ? true : false,
          is_jrp: pathname == "jrp-payment" ? true : false,
        }}
        rows={data?.data?.results ?? []}
        count={data?.data?.count ?? 10}
        form={<PaymentForm />}
      />
    </>
  );
};

export default index;
